import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  FileAudio,
  Info,
  Library,
  Lightbulb,
  ListChecks,
  MessageSquare,
  Mic,
  Music,
  Pause,
  Play,
  PlusCircle,
  Sparkles,
  Square,
  Trash2,
  UploadCloud,
  Volume2,
  Wand2,
  X,
} from "lucide-react";
import { Select } from "antd";
import { getDictDataListSimpleApi } from "../../service/dict";
import { postTimbreDesignCloneSaveApi, postTimbreDesignCloneStartApi } from "../../service/timbreDesign";
import { useAlert } from "../../hooks/useAlert";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { AlertMessage } from "../../components/common/AlertMessage";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { triggerPointsRefresh } from "../../service/points";
import { triggerMembershipRefresh } from "../../service/membership";
import { WavEncoder } from "./WavEncoder";
import "./VoiceClone.scss";

const CLONE_TEXT = "科技的浪潮正在重塑我们的世界，而声音，是传递情感最温情的介质。";
const ALLOWED_EXTS = ["m4a", "mp3", "wav"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MIN_DURATION = 10;
const MAX_DURATION = 60;

type Mode = "upload" | "record";
type TemplateVariant = "original" | "cloned";

const DEMO_AUDIOS = [
  { id: "demo1", label: "示例 1 - 男声", desc: "清晰朗读 · 15 秒 · wav / 单声道", src: "/Example1.mp3" },
  { id: "demo2", label: "示例 2 - 女声", desc: "日常对话 · 20 秒 · mp3 / 单声道", src: "/Example2.mp3" },
];

const VOICE_TEMPLATES = [
  {
    id: "night-storyteller",
    title: "雨夜叙事者",
    description: "低缓而有呼吸感的中性声线，细节克制，适合悬疑故事与沉浸式阅读。",
    specs: ["低沉", "电影感", "慢语速"],
    cover: "/assets/audio-templates/audiobook-night-train.png",
    originalAudioSrc: "/模板_有声读物温柔女性.wav",
    clonedAudioSrc: "/克隆_有声读物温柔女性.mp3",
    sampleText: "凌晨两点十七分，最后一班列车穿过雨幕。林野合上旧书，却发现书页间夹着一张写给三十年后的车票。",
  },
  {
    id: "robot-friend",
    title: "元气机械伙伴",
    description: "清亮、俏皮又带一点电子质感，适合动画角色、科技栏目与趣味配音。",
    specs: ["明快", "角色感", "高表现力"],
    cover: "/assets/audio-templates/video-dubbing-robot.png",
    originalAudioSrc: "/模板_配音视频.wav",
    clonedAudioSrc: "/克隆_配音视频.mp3",
    sampleText: "录音倒计时开始！三、二、一，等等，我的情绪模块还没加载。好吧，现在请听一位机器人如何认真地讲冷笑话。",
  },
  {
    id: "sunny-creator",
    title: "阳光种草主理人",
    description: "自然上扬、节奏利落，兼顾亲切感和信息密度，适合好物分享与生活攻略。",
    specs: ["年轻", "亲和", "快节奏"],
    cover: "/assets/audio-templates/short-video-creator.png",
    originalAudioSrc: "/模板_口播.wav",
    clonedAudioSrc: "/克隆_口播.mp3",
    sampleText: "别急着划走，三十秒教你把普通柠檬水调出夏日感：冰块加到七分满，再放一小撮海盐，清爽度直接翻倍。",
  },
  {
    id: "coast-announcer",
    title: "海岸站务播音员",
    description: "清晰稳健、停连准确，在公共空间中保持足够穿透力，适合交通与服务通知。",
    specs: ["清晰", "可信赖", "标准语速"],
    cover: "/assets/audio-templates/announcement-coast-train.png",
    originalAudioSrc: "/模板_广播.wav",
    clonedAudioSrc: "/克隆_广播.mp3",
    sampleText: "各位旅客请注意，受近海浓雾影响，蓝湾线今日十点前调整为二十分钟一班。请留意站台指引，合理安排出行。",
  },
];

export const VoiceClone: FC = () => {
  const { alertConfig, showError, showInfo, showWarning, hideAlert } = useAlert();
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW) === true;
  const [mode, setMode] = useState<Mode>("upload");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const [recordBlob, setRecordBlob] = useState<Blob | null>(null);
  const [isPlayingRecord, setIsPlayingRecord] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptNodeRef = useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const wavEncoderRef = useRef<WavEncoder | null>(null);
  const recordStartTimeRef = useRef(0);
  const recordPausedTimeRef = useRef(0);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [isPlayingUpload, setIsPlayingUpload] = useState(false);
  const uploadAudioRef = useRef<HTMLAudioElement | null>(null);

  const [playingDemo, setPlayingDemo] = useState<string | null>(null);
  const demoAudioRef = useRef<HTMLAudioElement | null>(null);
  const templateAudioRef = useRef<HTMLAudioElement | null>(null);
  const templateAudioKeyRef = useRef<string | null>(null);
  const [isRequirementsOpen, setIsRequirementsOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [playingTemplateKey, setPlayingTemplateKey] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneResult, setCloneResult] = useState(false);
  const cloneStartTimeRef = useRef<number | null>(null);
  const [clonePreview, setClonePreview] = useState<TimbreDesign.CloneStartData | null>(null);

  const [registerName, setRegisterName] = useState("");
  const [registerDescription, setRegisterDescription] = useState("");
  const [registerLanguage, setRegisterLanguage] = useState("");
  const [registerGender, setRegisterGender] = useState("");
  const [registerAge, setRegisterAge] = useState("");
  const [registerCategory, setRegisterCategory] = useState("");
  const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
  const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
  const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
  const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);
  const [isSavingVoice, setIsSavingVoice] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const dictOptionsToSelectOptions = useCallback((items: Dict.DictItem[]) => {
    return items.map((item) => ({
      value: String(item.value ?? ""),
      label: item.name,
    }));
  }, []);

  const langSelectOptions = useMemo(() => dictOptionsToSelectOptions(langOptions), [dictOptionsToSelectOptions, langOptions]);
  const genderSelectOptions = useMemo(() => dictOptionsToSelectOptions(genderOptions), [dictOptionsToSelectOptions, genderOptions]);
  const ageSelectOptions = useMemo(() => dictOptionsToSelectOptions(ageOptions), [ageOptions, dictOptionsToSelectOptions]);
  const timbreSelectOptions = useMemo(() => dictOptionsToSelectOptions(timbreOptions), [dictOptionsToSelectOptions, timbreOptions]);

  const canClone = useCallback(() => {
    return (mode === "upload" && uploadedFile !== null) || (mode === "record" && recordBlob !== null);
  }, [mode, uploadedFile, recordBlob]);

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const getCloneAudioFile = useCallback(() => {
    const rawFile = mode === "upload" ? uploadedFile : recordBlob;
    return rawFile instanceof File
      ? rawFile
      : rawFile
        ? new File([rawFile], "recording.wav", { type: rawFile.type || "audio/wav" })
        : null;
  }, [mode, uploadedFile, recordBlob]);

  const validateDuration = useCallback(() => {
    const duration = mode === "upload" ? fileDuration : recordDuration;
    if (duration != null && (duration < MIN_DURATION || duration > MAX_DURATION)) {
      showWarning(`音频时长需在 ${MIN_DURATION} ~ ${MAX_DURATION} 秒之间，当前：${duration.toFixed(1)} 秒`);
      return false;
    }
    return true;
  }, [mode, fileDuration, recordDuration, showWarning]);

  const destroyDemoAudio = useCallback(() => {
    if (demoAudioRef.current) {
      demoAudioRef.current.pause();
      demoAudioRef.current.src = "";
      demoAudioRef.current = null;
    }
    setPlayingDemo(null);
  }, []);

  const closeRequirementsModal = useCallback(() => {
    destroyDemoAudio();
    setIsRequirementsOpen(false);
  }, [destroyDemoAudio]);

  useEffect(() => {
    let mounted = true;
    const pickValue = (items: Dict.DictItem[]) => items.length > 0 ? String(items[0].value ?? "") : "";
    const fetchDicts = async () => {
      try {
        const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
          getDictDataListSimpleApi("target_language"),
          getDictDataListSimpleApi("system_user_sex"),
          getDictDataListSimpleApi("age_category"),
          getDictDataListSimpleApi("voice_type"),
        ]);
        if (!mounted) return;
        const nextLangOptions = langRes.code === 0 && Array.isArray(langRes.data) ? langRes.data : [];
        const nextGenderOptions = genderRes.code === 0 && Array.isArray(genderRes.data) ? genderRes.data : [];
        const nextAgeOptions = ageRes.code === 0 && Array.isArray(ageRes.data) ? ageRes.data : [];
        const nextTimbreOptions = timbreRes.code === 0 && Array.isArray(timbreRes.data) ? timbreRes.data : [];
        setLangOptions(nextLangOptions);
        setGenderOptions(nextGenderOptions);
        setAgeOptions(nextAgeOptions);
        setTimbreOptions(nextTimbreOptions);
        setRegisterLanguage((current) => current || pickValue(nextLangOptions));
        setRegisterGender((current) => current || pickValue(nextGenderOptions));
        setRegisterAge((current) => current || pickValue(nextAgeOptions));
        setRegisterCategory((current) => current || pickValue(nextTimbreOptions));
      } catch (error) {
        console.error("获取音色登记字典失败:", error);
      }
    };
    void fetchDicts();
    return () => {
      mounted = false;
    };
  }, []);

  const toggleDemo = useCallback((id: string, src: string) => {
    if (playingDemo === id && demoAudioRef.current) {
      demoAudioRef.current.pause();
      setPlayingDemo(null);
      return;
    }
    if (demoAudioRef.current) {
      demoAudioRef.current.pause();
      demoAudioRef.current = null;
    }
    const audio = new Audio(src);
    demoAudioRef.current = audio;
    audio.onended = () => setPlayingDemo(null);
    audio.onerror = () => setPlayingDemo(null);
    audio.play().catch(() => setPlayingDemo(null));
    setPlayingDemo(id);
  }, [playingDemo]);

  const destroyTemplateAudio = useCallback(() => {
    const audio = templateAudioRef.current;
    if (audio) {
      audio.pause();
      audio.onended = null;
      audio.onerror = null;
      audio.onpause = null;
      audio.src = "";
      audio.load();
      templateAudioRef.current = null;
    }
    templateAudioKeyRef.current = null;
    setPlayingTemplateKey(null);
  }, []);

  const playTemplateAudio = useCallback((template: typeof VOICE_TEMPLATES[number], variant: TemplateVariant) => {
    const key = `${template.id}-${variant}`;
    if (templateAudioKeyRef.current === key && templateAudioRef.current) {
      destroyTemplateAudio();
      return;
    }

    destroyTemplateAudio();

    const src = variant === "original" ? template.originalAudioSrc : template.clonedAudioSrc;
    const audio = new Audio(src);
    templateAudioRef.current = audio;
    templateAudioKeyRef.current = key;
    setPlayingTemplateKey(key);

    const clearIfCurrent = () => {
      if (templateAudioRef.current !== audio || templateAudioKeyRef.current !== key) return false;
      audio.onended = null;
      audio.onerror = null;
      templateAudioRef.current = null;
      templateAudioKeyRef.current = null;
      setPlayingTemplateKey(null);
      return true;
    };

    audio.onended = () => {
      clearIfCurrent();
    };
    audio.onerror = () => {
      if (clearIfCurrent()) showError("音频播放失败，请稍后重试");
    };
    audio.play().catch(() => {
      if (clearIfCurrent()) showError("音频播放失败，请稍后重试");
    });
  }, [destroyTemplateAudio, showError]);

  const processFile = useCallback((file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTS.includes(ext)) {
      showWarning("不支持的音频格式，请上传 m4a、mp3 或 wav 文件");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showWarning("文件大小超过 10MB 限制");
      return;
    }
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.addEventListener("loadedmetadata", () => {
      const dur = audio.duration;
      URL.revokeObjectURL(url);
      if (dur < MIN_DURATION || dur > MAX_DURATION) {
        showWarning(`音频时长需在 ${MIN_DURATION} ~ ${MAX_DURATION} 秒之间，当前：${dur.toFixed(1)} 秒`);
        return;
      }
      setUploadedFile(file);
      setFileDuration(dur);
      setCloneResult(false);
      setClonePreview(null);
    });
    audio.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      setUploadedFile(file);
      setFileDuration(null);
      setCloneResult(false);
      setClonePreview(null);
    });
    audio.src = url;
  }, [showWarning]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) processFile(event.target.files[0]);
  }, [processFile]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) processFile(event.dataTransfer.files[0]);
  }, [processFile]);

  const removeFile = useCallback(() => {
    if (uploadAudioRef.current) {
      uploadAudioRef.current.pause();
      uploadAudioRef.current.src = "";
      uploadAudioRef.current = null;
    }
    setIsPlayingUpload(false);
    setUploadedFile(null);
    setFileDuration(null);
    setCloneResult(false);
    setClonePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const togglePlayUpload = useCallback(() => {
    if (!uploadedFile) return;
    if (isPlayingUpload && uploadAudioRef.current) {
      uploadAudioRef.current.pause();
      setIsPlayingUpload(false);
      return;
    }
    if (uploadAudioRef.current) {
      uploadAudioRef.current.play().catch(() => {});
      setIsPlayingUpload(true);
      return;
    }
    const url = URL.createObjectURL(uploadedFile);
    const audio = new Audio(url);
    uploadAudioRef.current = audio;
    audio.onended = () => {
      setIsPlayingUpload(false);
      URL.revokeObjectURL(url);
      uploadAudioRef.current = null;
    };
    audio.onerror = () => {
      setIsPlayingUpload(false);
      URL.revokeObjectURL(url);
      uploadAudioRef.current = null;
    };
    audio.play().catch(() => setIsPlayingUpload(false));
    setIsPlayingUpload(true);
  }, [uploadedFile, isPlayingUpload]);

  const stopRecording = useCallback(() => {
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current.onaudioprocess = null;
      scriptNodeRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }

    if (wavEncoderRef.current) {
      const dur = Math.floor((Date.now() - recordStartTimeRef.current - recordPausedTimeRef.current) / 1000);
      const wavBlob = wavEncoderRef.current.encode();
      wavEncoderRef.current = null;
      setRecordBlob(wavBlob);
      setRecordDuration(dur);
      setCloneResult(false);
      setClonePreview(null);
      if (dur < MIN_DURATION || dur > MAX_DURATION) {
        showWarning(`录音时长需在 ${MIN_DURATION} ~ ${MAX_DURATION} 秒之间，当前：${dur.toFixed(1)} 秒`);
      }
    }
    setIsRecording(false);
  }, [showWarning]);

  const startRecording = useCallback(async () => {
    try {
      const sampleRate = 16000;
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { sampleRate, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate });
      const source = audioCtx.createMediaStreamSource(stream);
      const gain = audioCtx.createGain();
      gain.gain.value = 1;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 128;
      const scriptNode = audioCtx.createScriptProcessor(4096, 1, 1);
      const encoder = new WavEncoder(sampleRate, 1);

      scriptNode.onaudioprocess = (event) => {
        encoder.addSamples(new Float32Array(event.inputBuffer.getChannelData(0)));
      };

      source.connect(gain);
      gain.connect(analyser);
      analyser.connect(scriptNode);
      scriptNode.connect(audioCtx.destination);

      audioContextRef.current = audioCtx;
      mediaStreamRef.current = stream;
      scriptNodeRef.current = scriptNode;
      gainNodeRef.current = gain;
      analyserRef.current = analyser;
      wavEncoderRef.current = encoder;
      recordStartTimeRef.current = Date.now();
      recordPausedTimeRef.current = 0;

      setIsRecording(true);
      setRecordDuration(0);
      setCloneResult(false);
      setClonePreview(null);

      recordTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordStartTimeRef.current - recordPausedTimeRef.current) / 1000);
        setRecordDuration(elapsed);
        if (elapsed >= MAX_DURATION) stopRecording();
      }, 200);
    } catch (error) {
      console.error("麦克风异常:", error);
      showError("无法访问麦克风，请检查浏览器权限设置");
    }
  }, [showError, stopRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording();
    else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const destroyPreviewAudio = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = "";
      previewAudioRef.current = null;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setIsPlayingRecord(false);
  }, []);

  const togglePlayRecord = useCallback(() => {
    if (!recordBlob) return;
    if (isPlayingRecord && previewAudioRef.current) {
      previewAudioRef.current.pause();
      setIsPlayingRecord(false);
      return;
    }
    if (!isPlayingRecord && previewAudioRef.current) {
      previewAudioRef.current.play().catch(() => {});
      setIsPlayingRecord(true);
      return;
    }
    destroyPreviewAudio();
    const url = URL.createObjectURL(recordBlob);
    previewUrlRef.current = url;
    const audio = new Audio(url);
    previewAudioRef.current = audio;
    audio.onended = () => {
      setIsPlayingRecord(false);
      destroyPreviewAudio();
    };
    audio.onerror = () => {
      setIsPlayingRecord(false);
      destroyPreviewAudio();
    };
    audio.play().catch(() => {
      setIsPlayingRecord(false);
      destroyPreviewAudio();
    });
    setIsPlayingRecord(true);
  }, [recordBlob, isPlayingRecord, destroyPreviewAudio]);

  const removeRecording = useCallback(() => {
    destroyPreviewAudio();
    setRecordBlob(null);
    setRecordDuration(0);
    setIsRecording(false);
    setCloneResult(false);
    setClonePreview(null);
  }, [destroyPreviewAudio]);
  const resetCloneWorkspace = useCallback(() => {
    if (uploadAudioRef.current) {
      uploadAudioRef.current.pause();
      uploadAudioRef.current.src = "";
      uploadAudioRef.current = null;
    }
    destroyPreviewAudio();
    destroyTemplateAudio();
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current.onaudioprocess = null;
      scriptNodeRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    wavEncoderRef.current = null;
    setMode("upload");
    setUploadedFile(null);
    setFileDuration(null);
    setIsPlayingUpload(false);
    setRecordBlob(null);
    setRecordDuration(0);
    setIsRecording(false);
    setIsPlayingRecord(false);
    setCloneResult(false);
    setClonePreview(null);
    setRegisterName("");
    setRegisterLanguage(langSelectOptions[0]?.value ?? "");
    setRegisterGender(genderSelectOptions[0]?.value ?? "");
    setRegisterAge(ageSelectOptions[0]?.value ?? "");
    setRegisterCategory(timbreSelectOptions[0]?.value ?? "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [ageSelectOptions, destroyPreviewAudio, destroyTemplateAudio, genderSelectOptions, langSelectOptions, timbreSelectOptions]);
  const startClone = useCallback(async () => {
    if (!canClone() || !validateDuration()) return;
    const audioFile = getCloneAudioFile();
    if (!audioFile) return;
    setIsCloning(true);
    cloneStartTimeRef.current = Date.now();
    window.setTimeout(() => {
      if (cloneStartTimeRef.current && Date.now() - cloneStartTimeRef.current >= 2000) {
        triggerMembershipRefresh();
      }
    }, 2000);
    try {
      const response = await postTimbreDesignCloneStartApi({
        text: CLONE_TEXT,
        audioFile,
      });
      if (response?.code !== 0) {
        if (response?.msg) showError(response.msg);
        return;
      }
      if (!response?.data?.audioPath || !response?.data?.cosRefAudioUrl) {
        return;
      }
      setClonePreview(response.data);
      triggerPointsRefresh();
      if (!cloneStartTimeRef.current || Date.now() - cloneStartTimeRef.current >= 2000) {
        triggerMembershipRefresh();
      }
      setCloneResult(true);
    } catch (error) {
      console.error("音色克隆失败:", error);
    } finally {
      setIsCloning(false);
    }
  }, [canClone, getCloneAudioFile, showError, validateDuration]);

  const saveVoiceToLibrary = useCallback(async () => {
    const voiceName = registerName.trim();
    if (!voiceName) return;
    if (!clonePreview?.audioPath || !clonePreview?.cosRefAudioUrl) {
      showError("请先完成音色克隆");
      return;
    }
    setIsSavingVoice(true);
    try {
      await postTimbreDesignCloneSaveApi({
        audioPath: clonePreview.audioPath,
        cosRefAudioUrl: clonePreview.cosRefAudioUrl,
        text: CLONE_TEXT,
        voiceName,
        description: registerDescription.trim(),
        langCode: registerLanguage,
        genderValue: registerGender,
        ageValue: registerAge,
        timbreValue: registerCategory,
      });
      setIsRegistrationOpen(false);
      setRegisterDescription("");
      resetCloneWorkspace();
      showInfo("已加入音色库");
    } catch (error: any) {
      showError(error?.message || "音色登记失败，请重试");
    } finally {
      setIsSavingVoice(false);
    }
  }, [clonePreview, registerAge, registerCategory, registerDescription, registerGender, registerLanguage, registerName, resetCloneWorkspace, showError, showInfo]);

  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      destroyPreviewAudio();
      if (demoAudioRef.current) {
        demoAudioRef.current.pause();
        demoAudioRef.current = null;
      }
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      if (audioContextRef.current) audioContextRef.current.close();
      if (uploadAudioRef.current) {
        uploadAudioRef.current.pause();
        uploadAudioRef.current.src = "";
        uploadAudioRef.current = null;
      }
      destroyTemplateAudio();
    };
  }, [destroyPreviewAudio, destroyTemplateAudio]);

  const shouldShowCloneCost = Boolean(uploadedFile || recordBlob);

  return (
    <div className="vc-page">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <div className="vc-studio-shell">
        <section className="vc-studio-hero">
          <div className="vc-section-kicker">Voice Clone</div>
          <h1>音色克隆</h1>
          <p>上传清晰人声音频或直接录音，先完成克隆试听，满意后再登记到你的音色库。</p>
        </section>

        <div className="vc-clone-workspace">
          <div className="vc-card vc-card-hover vc-clone-card">
            <div className="vc-clone-card-head">
              <div className="vc-clone-mode-tabs">
                <button className={`vc-mode-tab ${mode === "upload" ? "active" : ""}`} onClick={() => setMode("upload")}>
                  <UploadCloud size={16} />上传文件
                </button>
                <button className={`vc-mode-tab ${mode === "record" ? "active" : ""}`} onClick={() => setMode("record")}>
                  <MessageSquare size={16} />音频录制
                </button>
              </div>
              <button className="vc-usage-link" onClick={() => setIsRequirementsOpen(true)}>
                使用说明<ChevronRight size={14} />
              </button>
            </div>

            {mode === "upload" && (
              <div className="vc-tab-content">
                {!uploadedFile ? (
                  <div
                    className={`vc-upload-zone ${isDragging ? "dragover" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                  >
                    <div className="vc-upload-icon"><UploadCloud size={22} /></div>
                    <div className="vc-upload-title">点击上传或拖拽音频文件到此处</div>
                    <div className="vc-upload-hint">支持 m4a、mp3、wav 格式 · 10~60 秒 · 最大 10MB</div>
                    <input ref={fileInputRef} type="file" accept=".m4a,.mp3,.wav,audio/m4a,audio/mp3,audio/wav" onChange={handleFileSelect} />
                  </div>
                ) : (
                  <div className="vc-file-card">
                    <div className="vc-file-icon vc-file-icon-dark"><Music size={20} /></div>
                    <div className="vc-file-info">
                      <div className="vc-file-name">{uploadedFile.name}</div>
                      <div className="vc-file-meta">
                        {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                        {fileDuration != null && ` · ${fileDuration.toFixed(0)} 秒`}
                        {` · ${uploadedFile.name.split(".").pop()?.toUpperCase()}`}
                      </div>
                    </div>
                    <div className="vc-file-actions">
                      <button onClick={togglePlayUpload} title={isPlayingUpload ? "暂停" : "试听"}>
                        {isPlayingUpload ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button className="btn-delete" onClick={removeFile} title="删除"><Trash2 size={16} /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === "record" && (
              <div className="vc-tab-content">
                <div className="vc-record-panel">
                  <button className={`vc-record-circle ${isRecording ? "recording" : ""}`} onClick={toggleRecording}>
                    {isRecording ? <Square size={26} /> : <Mic size={28} />}
                  </button>
                  <div className="vc-record-timer">{formatDuration(recordDuration)}</div>
                  <div className="vc-record-hint">
                    {isRecording ? "正在录音..." : recordBlob ? "点击上方按钮重新录音" : "点击上方按钮开始录音"}
                  </div>
                </div>
                {recordBlob && (
                  <div className="vc-file-card vc-record-file">
                    <div className="vc-file-icon vc-file-icon-red"><Mic size={20} /></div>
                    <div className="vc-file-info">
                      <div className="vc-file-name">录音文件</div>
                      <div className="vc-file-meta">{(recordBlob.size / (1024 * 1024)).toFixed(2)} MB · {recordDuration} 秒 · WAV</div>
                    </div>
                    <div className="vc-file-actions">
                      <button onClick={togglePlayRecord} title={isPlayingRecord ? "暂停" : "试听"}>
                        {isPlayingRecord ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button className="btn-delete" onClick={removeRecording} title="删除"><Trash2 size={16} /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="vc-clone-footer">
              {hasPoints && !cloneResult && shouldShowCloneCost && (
                <div className="vc-clone-cost-estimate" title="按克隆次数扣除">
                  <CoinsEstimateIcon />预计扣除 20 积分
                </div>
              )}
              {!cloneResult && (
                <button className="vc-btn-generate" disabled={!canClone() || isCloning} onClick={startClone}>
                  {isCloning ? <><span className="vc-spin"><Wand2 size={20} /></span>克隆中...</> : <><Wand2 size={20} />开始克隆</>}
                </button>
              )}
              {cloneResult && (
                <div className="vc-result-banner">
                  <div className="vc-success-icon"><Check size={22} /></div>
                  <div className="vc-result-copy">
                    <div>音色克隆完成</div>
                    <p>试听满意后可登记到音色库，方便后续在文本转语音中复用</p>
                  </div>
                  <button className="vc-btn-use" onClick={() => setIsRegistrationOpen(true)}>音色登记<ArrowRight size={14} /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="vc-work-section">
          <div className="vc-work-panel">
            <div className="vc-voice-template-grid">
              {VOICE_TEMPLATES.map((template) => {
                const originalKey = `${template.id}-original`;
                const clonedKey = `${template.id}-cloned`;
                return (
                  <div className="vc-voice-template-card" key={template.id}>
                    <div className="vc-voice-template-cover" style={{ backgroundImage: `url(${template.cover})` }}>
                      <div className="vc-voice-template-body">
                        <h4>{template.title}</h4>
                        <p>{template.description}</p>
                        <div className="vc-voice-template-specs">
                          {template.specs.map((spec) => <span key={spec}>{spec}</span>)}
                        </div>
                      </div>
                    </div>
                    <div className="vc-voice-template-dual">
                      <div className="vc-voice-template-side">
                        <button className={`vc-voice-template-play-btn ${playingTemplateKey === originalKey ? "is-playing" : ""}`} onClick={() => playTemplateAudio(template, "original")}>
                          <Volume2 size={15} /><span>{playingTemplateKey === originalKey ? "播放中" : "原语音"}</span>
                        </button>
                      </div>
                      <div className="vc-voice-template-side cloned">
                        <button className={`vc-voice-template-play-btn ${playingTemplateKey === clonedKey ? "is-playing" : ""}`} onClick={() => playTemplateAudio(template, "cloned")}>
                          <Sparkles size={15} /><span>{playingTemplateKey === clonedKey ? "播放中" : "克隆后语音"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {isRequirementsOpen && (
        <div className="vc-modal-backdrop show" onClick={closeRequirementsModal}>
          <div className="vc-requirements-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="vc-requirements-modal-header">
              <div>
                <h3><ListChecks size={20} />音频录制要求</h3>
                <p>请参考下方示例，确保音频符合以下规范</p>
              </div>
              <button className="vc-requirements-modal-close" onClick={closeRequirementsModal} title="关闭"><X size={20} /></button>
            </div>
            <div className="vc-requirements-body">
              <div className="vc-requirement-card">
                <div className="vc-req-item">
                  <div className="vc-req-icon vc-req-icon-blue"><Clock size={16} /></div>
                  <div className="vc-req-text"><div className="vc-req-title">音频时长</div><div className="vc-req-desc">支持上传 <strong>10 ~ 60 秒</strong>，文件最大不超过 <strong>10MB</strong></div></div>
                </div>
                <div className="vc-req-item">
                  <div className="vc-req-icon vc-req-icon-pink"><FileAudio size={16} /></div>
                  <div className="vc-req-text"><div className="vc-req-title">音频格式</div><div className="vc-req-desc">支持 <strong>m4a、mp3、wav</strong> 格式</div></div>
                </div>
                <div className="vc-req-item">
                  <div className="vc-req-icon vc-req-icon-green"><Activity size={16} /></div>
                  <div className="vc-req-text"><div className="vc-req-title">采样率与声道</div><div className="vc-req-desc"><strong>16000 Hz</strong> · <strong>单声道</strong> · 人声清晰无明显噪音</div></div>
                </div>
              </div>

              <div className="vc-demo-grid">
                {DEMO_AUDIOS.map((item) => (
                  <button key={item.id} className="vc-demo-audio" onClick={() => toggleDemo(item.id, item.src)}>
                    <span className="vc-play-mini">{playingDemo === item.id ? <Pause size={14} /> : <Play size={14} />}</span>
                    <span>
                      <strong>{item.label}</strong>
                      <em>{item.desc}</em>
                    </span>
                  </button>
                ))}
              </div>

              <div className="vc-tip-card">
                <Lightbulb size={20} />
                <div>
                  <h4>温馨提示</h4>
                  <ul>
                    <li>请在安静环境中录制，避免背景噪音</li>
                    <li>语速保持自然，不要过快或过慢</li>
                    <li>使用高质量麦克风可获得更佳效果</li>
                    <li>克隆后的音色将存入您的个人音色库</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isRegistrationOpen && (
        <div className="vc-modal-backdrop show" onClick={() => setIsRegistrationOpen(false)}>
          <div className="vc-requirements-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="vc-requirements-modal-header">
              <div>
                <h3><Library size={20} />音色登记</h3>
                <p>给克隆结果补充信息，登记后可在文本转语音中直接选用</p>
              </div>
              <button className="vc-requirements-modal-close" onClick={() => setIsRegistrationOpen(false)} title="关闭"><X size={20} /></button>
            </div>
            <div className="vc-requirements-body">
              <div className="vc-registration-intro">
                <div className="vc-registration-intro-icon"><Info size={16} /></div>
                <div>
                  <div className="vc-registration-intro-title">登记说明</div>
                  <div className="vc-registration-intro-desc">补充名称、语种和标签后，该音色将加入你的个人音色库，后续可直接在文本转语音里选择使用。</div>
                </div>
              </div>
              <div className="vc-clone-form-grid">
                <div className="vc-form-full">
                  <label className="vc-form-label">音色名称 <span className="required">*</span></label>
                  <input className="vc-form-input" value={registerName} onChange={(event) => setRegisterName(event.target.value)} placeholder="例如：纪录片旁白、张三克隆音色" maxLength={30} />
                </div>
                <div className="vc-form-full">
                  <label className="vc-form-label">音色描述</label>
                  <textarea
                    className="vc-form-input vc-form-textarea"
                    value={registerDescription}
                    onChange={(event) => setRegisterDescription(event.target.value)}
                    placeholder="请输入个人音色描述"
                    maxLength={500}
                    rows={3}
                  />
                </div>
                <div>
                  <label className="vc-form-label">语种 <span className="required">*</span></label>
                  <Select
                    className="vc-form-select-control"
                    popupClassName="vc-form-select-dropdown"
                    getPopupContainer={(trigger) => trigger.closest(".vc-requirements-modal") || document.body}
                    value={registerLanguage || undefined}
                    onChange={setRegisterLanguage}
                    placeholder="请选择语种"
                    options={langSelectOptions}
                  />
                </div>
                <div>
                  <label className="vc-form-label">性别 <span className="required">*</span></label>
                  <Select
                    className="vc-form-select-control"
                    popupClassName="vc-form-select-dropdown"
                    getPopupContainer={(trigger) => trigger.closest(".vc-requirements-modal") || document.body}
                    value={registerGender || undefined}
                    onChange={setRegisterGender}
                    placeholder="请选择性别"
                    options={genderSelectOptions}
                  />
                </div>
                <div>
                  <label className="vc-form-label">年龄 <span className="required">*</span></label>
                  <Select
                    className="vc-form-select-control"
                    popupClassName="vc-form-select-dropdown"
                    getPopupContainer={(trigger) => trigger.closest(".vc-requirements-modal") || document.body}
                    value={registerAge || undefined}
                    onChange={setRegisterAge}
                    placeholder="请选择年龄"
                    options={ageSelectOptions}
                  />
                </div>
                <div>
                  <label className="vc-form-label">类别 <span className="required">*</span></label>
                  <Select
                    className="vc-form-select-control"
                    popupClassName="vc-form-select-dropdown"
                    getPopupContainer={(trigger) => trigger.closest(".vc-requirements-modal") || document.body}
                    value={registerCategory || undefined}
                    onChange={setRegisterCategory}
                    placeholder="请选择类别"
                    options={timbreSelectOptions}
                  />
                </div>
              </div>
              <button className="vc-btn-generate vc-save-voice-btn" disabled={!registerName.trim() || !registerLanguage || !registerGender || !registerAge || !registerCategory || isSavingVoice} onClick={saveVoiceToLibrary}>
                {isSavingVoice ? <><span className="vc-spin"><PlusCircle size={20} /></span>加入中...</> : <><PlusCircle size={20} />加入音色库</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};